import PropTypes from 'prop-types'
import {
  countKeyServices,
  countKeyInfrastructure,
} from '../../utils/facilityUtils'

function FacilityCard({ facility, onClick }) {
  // Count key services consistently
  const services = countKeyServices(facility)

  // Count infrastructure consistently
  const infrastructure = countKeyInfrastructure(facility)

  const getLevelBadgeClass = level => {
    switch (level) {
      case 'HCII':
        return 'facility-badge facility-badge-hc2'
      case 'HCIII':
        return 'facility-badge facility-badge-hc3'
      case 'HCIV':
        return 'facility-badge facility-badge-hc4'
      case 'Hospital':
        return 'facility-badge facility-badge-hospital'
      case 'Clinic':
        return 'facility-badge facility-badge-clinic'
      default:
        return 'facility-badge bg-surface-100 text-surface-700 border-surface-200'
    }
  }

  return (
    <div className="card card-interactive group animate-fade-in p-3">
      {/* Facility Header */}
      <div className="mb-4">
        <h3 className="font-semibold text-surface-900 text-lg leading-tight group-hover:text-primary-700 transition-colors capitalize">
          {facility.name}
        </h3>
        <div className="flex items-center justify-between mt-3">
          <span className={getLevelBadgeClass(facility.level)}>
            {facility.level}
          </span>
          <span className="text-xs text-surface-500 font-medium">
            {facility.ownership}
          </span>
        </div>
      </div>

      {/* Location */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-surface-600">
          <span className="text-primary-500 mr-1">📍</span>
          <span className="font-medium">{facility.parish}</span>
        </div>
        <div className="text-xs text-surface-500 mt-1 ml-6">
          {facility.district} District
        </div>
        <div className="text-xs text-surface-400 mt-1 ml-6">
          {facility.subcounty}
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-3">
          <div className="text-xl font-bold text-primary-600">{services}</div>
          <div className="text-xs text-primary-700 font-medium">
            Key Services
          </div>
        </div>
        <div className="text-center bg-gradient-to-br from-surface-50 to-surface-100 rounded-lg p-3">
          <div className="text-xl font-bold text-surface-700">
            {infrastructure}
          </div>
          <div className="text-xs text-surface-600 font-medium">
            Infrastructure
          </div>
        </div>
      </div>

      {/* Officer */}
      {facility.officer_in_charge && (
        <div className="mb-4 pb-4 border-b border-surface-100">
          <div className="text-xs text-surface-500 font-medium">
            Officer in Charge
          </div>
          <div className="text-sm font-semibold text-surface-700 mt-1 capitalize">
            {facility.officer_in_charge}
          </div>
        </div>
      )}

      {/* Quick Services List */}
      <div className="mb-5">
        <div className="text-xs text-surface-500 font-medium mb-2">
          Key Services:
        </div>
        <div className="flex flex-wrap gap-2">
          {facility.immunization_services === 'Yes' && (
            <span className="inline-flex items-center px-2 py-1 bg-status-success/10 text-status-success rounded-md text-xs font-medium">
              💉 Immunization
            </span>
          )}
          {facility.laboratory === 'Yes' && (
            <span className="inline-flex items-center px-2 py-1 bg-status-info/10 text-status-info rounded-md text-xs font-medium">
              🔬 Lab
            </span>
          )}
          {facility.maternal_and_child_health_services === 'Yes' && (
            <span className="inline-flex items-center px-2 py-1 bg-pink-100 text-pink-700 rounded-md text-xs font-medium">
              👶 MCH
            </span>
          )}
          {services > 3 && (
            <span className="inline-flex items-center px-2 py-1 bg-surface-100 text-surface-600 rounded-md text-xs font-medium">
              +{services - 3} more
            </span>
          )}
        </div>
      </div>

      {/* View Details Button */}
      <button
        onClick={() => onClick?.(facility)}
        className="btn btn-primary w-full group-hover:bg-primary-600 transform group-hover:scale-[1.02] transition-all duration-200"
      >
        <span className="text-sm font-semibold">View Details</span>
      </button>
    </div>
  )
}

FacilityCard.propTypes = {
  facility: PropTypes.shape({
    name: PropTypes.string,
    level: PropTypes.string,
    ownership: PropTypes.string,
    parish: PropTypes.string,
    district: PropTypes.string,
    subcounty: PropTypes.string,
    officer_in_charge: PropTypes.string,
    immunization_services: PropTypes.string,
    laboratory: PropTypes.string,
    maternal_and_child_health_services: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
}

export default FacilityCard
